import "./App.css";

import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";

import Notfound from "./pages/error/Notfound";
import ForgotPassword from "./pages/forgot-password/ForgotPassword";
import Home from "./pages/home/Home";
import Login from "./pages/login/Login";
import { ThemeModeProvider, useThemeMode } from "./theme/ThemeContext";
import { ConfigProvider } from "antd";
import "antd/dist/reset.css";
import ResetPassword from "./pages/reset-password/ResetPassword";
import { Bounce, ToastContainer } from 'react-toastify';
import { ThemeProvider, Theme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { getAppLocale, isRtl } from "./locales/appLocale";

const brandTheme = (outer: Theme): Theme => ({
  ...outer,
  palette: {
    ...outer.palette,
    primary: { ...outer.palette.primary, main: "#ba2156", dark: "#9e1b4a" },
  },
  components: {
    ...outer.components,
    MuiButton: {
      ...outer.components?.MuiButton,
      styleOverrides: {
        ...outer.components?.MuiButton?.styleOverrides,
        root: { textTransform: "capitalize" },
      },
    },
  },
});

const AppContent = () => {
  const { mode } = useThemeMode();
  const { i18n } = useTranslation();
  const rtl = isRtl(i18n.language);
  return (
    <div className="App">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={rtl}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={mode}
        transition={Bounce}
        />
      <ThemeProvider theme={brandTheme}>
        <ConfigProvider
          locale={getAppLocale(i18n.language).antd}
          direction={rtl ? "rtl" : "ltr"}
          theme={{
            token: { colorPrimary: "#ba2156" },
            components: {
              Table: {
                headerBg: "#ba2156",
                headerColor: "#ffffff",
                headerSplitColor: "transparent",
                rowHoverBg: "rgba(186, 33, 86, 0.04)",
              },
            },
          }}
        >
          <BrowserRouter>
          <Routes>
            <Route path="/">
              <Route index element={<Home component="" />} />
              <Route path="admin" element={<Home component="admin" />} />
              <Route path="app-users" element={<Home component="userApp" />} />
              <Route path="app-users/:id" element={<Home component="userAppDetail" />} />
              <Route
                path="admin/:id"
                element={<Home component="adminDetail" />}
              />
              <Route
                path="dashboard"
                element={<Home component="dashboard" />}
              />
              <Route path="category" element={<Home component="category" />} />
              <Route
                path="category/:id"
                element={<Home component="categoryDetail" />}
              />
              <Route path="coupon" element={<Home component="coupon" />} />
              <Route
                path="help-support"
                element={<Home component="help-support" />}
              />
              <Route
                path="help-support/:id"
                element={<Home component="helpSupportDetail" />}
              />
              <Route
                path="notifications"
                element={<Home component="notifications" />}
              />
              <Route
                path="outlet-change-requests"
                element={<Home component="outletChangeRequests" />}
              />
              <Route
                path="outlet-change-requests/:id"
                element={<Home component="outletChangeRequestDetail" />}
              />
              <Route
                path="history-log"
                element={<Home component="history-log" />}
              />
              <Route path="map" element={<Home component="map" />} />
              <Route path="orders" element={<Home component="order" />} />
              <Route
                path="orders/:id"
                element={<Home component="orderDetail" />}
              />
              <Route
                path="setting"
                element={<Navigate to={"/setting/language"} />}
              />
              <Route path="salon" element={<Home component="salon" />} />
              <Route path="salon/add" element={<Home component="addSalon" />} />
              <Route
                path="salon/registration/:id"
                element={<Home component="editRegistrationSalon" />}
              />
              <Route
                path="salon/:id"
                element={<Home component="editSalon" />}
              />
              <Route
                path="setting/language"
                element={<Home component="language" />}
              />
              <Route
                path="setting/package"
                element={<Navigate to="/settings/packages" replace />}
              />
              <Route
                path="settings/packages"
                element={<Home component="packageList" />}
              />
              <Route
                path="settings/packages/create"
                element={<Home component="packageCreate" />}
              />
              <Route
                path="settings/packages/:id/edit"
                element={<Home component="packageEdit" />}
              />
              <Route
                path="settings/packages/:id/menus"
                element={<Home component="packageMenuConfig" />}
              />
              <Route
                path="settings/menus"
                element={<Home component="menuList" />}
              />
              <Route
                path="settings/menus/create"
                element={<Home component="menuCreate" />}
              />
              <Route
                path="settings/menus/:id/edit"
                element={<Home component="menuEdit" />}
              />
              <Route
                path="settings/menus/reorder"
                element={<Home component="menuReorder" />}
              />
              <Route
                path="setting/country"
                element={<Home component="country" />}
              />
              <Route
                path="setting/system-config"
                element={<Home component="systemConfig" />}
              />
              <Route
                path="setting/loyalty"
                element={<Home component="loyalty" />}
              />
              <Route
                path="setting/ai-chat"
                element={<Home component="aiChat" />}
              />
              <Route
                path="setting/billing-fees"
                element={<Home component="billingFeeConfig" />}
              />
              <Route
                path="setting/payment-gateways"
                element={<Home component="paymentGatewayConfig" />}
              />
              <Route
                path="setting/exchange-rates"
                element={<Home component="exchangeRateConfig" />}
              />
              <Route
                path="setting/cancellation-policies"
                element={<Home component="cancellationPolicyConfig" />}
              />
              <Route
                path="setting/refund-requests"
                element={<Home component="refundRequests" />}
              />
              <Route
                path="setting/system-configs"
                element={<Home component="systemConfigs" />}
              />
              <Route
                path="setting/multi-lang/app"
                element={<Home component="appTranslation" />}
              />
              <Route
                path="setting/multi-lang/salon"
                element={<Home component="salonTranslation" />}
              />
              <Route
                path="setting/message-codes"
                element={<Home component="errorMessages" />}
              />
              <Route
                path="setting/cancel-reasons"
                element={<Home component="cancelReasons" />}
              />
              <Route
                path="setting/country/:id"
                element={<Home component="countryDetail" />}
              />
              <Route path="setting/roles" element={<Home component="roles" />} />
              <Route
                path="review-moderation"
                element={<Home component="reviewModeration" />}
              />
            </Route>
            <Route path="login" element={<Login />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="reset-password" element={<ResetPassword />} />
            <Route path="*" element={<Notfound />} />
          </Routes>
          </BrowserRouter>
        </ConfigProvider>
      </ThemeProvider>
    </div>
  );
};

const App = () => (
  <ThemeModeProvider>
    <AppContent />
  </ThemeModeProvider>
);

export default App;
